import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import './Header.css';
import Navbar from "./Navbar";
import SecondaryNavbar from "./SecondaryNavbar";
import { JOINTS } from './heroTraces';

// Rinne im Klinker-Foto: Querstäbe zwischen zwei Kanten
const rail = Array.from({ length: 34 }, (_, k) => {
  const t = k / 33;
  return `M${Math.round(95 * t)} ${Math.round(590 + 660 * t)}L${Math.round(16 + 184 * t)} ${Math.round(512 + 738 * t)}`;
});

// Echte Referenzprojekte – Koordinaten im Bildraum 1000 × 1250, Marken in % des Rahmens
const PROJECTS = [
  {
    src: '/hero/eg-hero-1.jpg',
    tab: 'Terrasse',
    title: 'Terrasse in Großformatplatten',
    note: 'Bündig an Hecke und Gartenhaus',
    outline: ['M0 560L285 365L705 378L1000 605'],
    context: [
      'M248 180L408 72L570 190', 'M288 170V362H548V188', 'M428 180H502V356H428Z',
      'M330 176H392V256H330Z', 'M436 184H494V266H436Z',
      'M0 150L270 178', 'M165 160L220 410', 'M0 250L80 505',
      'M702 372C730 300 740 200 760 150C820 140 900 130 1000 128',
    ],
    datum: [190, 1010],
    marks: [
      { x: 50, y: 74, t: 'Großformatplatten', s: 'Im Reihenverband verlegt' },
      { x: 51, y: 31, t: 'Anschluss Gartenhaus', s: 'Bündig an die Kante gesetzt' },
    ],
  },
  {
    src: '/hero/eg-hero-2.jpg',
    tab: 'Naturstein',
    title: 'Polygonalplatten aus Naturstein',
    note: 'Handverlegt, Fuge für Fuge',
    outline: ['M0 612L225 516L600 490L806 514L864 1250'],
    context: [
      'M0 270L190 282L205 470L0 505', 'M180 276L240 272L266 500L214 512',
      'M20 145L375 225L372 255L40 190', 'M0 70L585 248', 'M240 240L600 312', 'M600 312V492',
      'M598 470H945C900 480 890 500 800 512',
    ],
    datum: [170, 900],
    marks: [
      { x: 40, y: 84, t: 'Polygonalplatten', s: 'Naturstein, handverlegt' },
      { x: 53, y: 62, t: 'Gerade Kante', s: 'Zum Beet hin geschnitten' },
    ],
  },
  {
    src: '/hero/eg-hero-3.jpg',
    tab: 'Klinker',
    title: 'Klinker im Fischgrätverband',
    note: 'Mit Granit-Einfassung und Entwässerungsrinne',
    outline: ['M50 395L468 320L474 224L690 238L772 345L985 478', 'M848 862L322 1082L228 700L50 395'],
    context: [
      'M205 0L215 340L465 305L462 0', 'M625 0L638 208L700 238L705 0',
      'M0 590L100 1250', 'M16 512L200 1250', ...rail,
    ],
    datum: [380, 640],
    marks: [
      { x: 52, y: 54, t: 'Klinker', s: 'Im Fischgrätverband' },
      { x: 12, y: 80, t: 'Entwässerungsrinne', s: 'Mit Granit-Einfassung' },
    ],
  },
];
const DRAW_MS = 2300;   // Tuschelinien zeichnen sich
const HOLD_MS = 6500;   // Ruhezeit, bevor das nächste Projekt kommt
const REST = 42;        // Ruheposition der Baulinie (% Entwurf)
const withPublic = (src) => `${process.env.PUBLIC_URL}${src}`;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const easeIO = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  return reduced;
}

// ── Animierter Zähler ─────────────────────────────────
function Counter({ to, suffix = '', start, instant }) {
  const [val, setVal] = useState(instant ? to : 0);
  useEffect(() => {
    if (instant) { setVal(to); return undefined; }
    if (!start) return undefined;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const k = Math.min(1, (now - t0) / 1400);
      setVal(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, start, instant]);
  return <span>{val}{suffix}</span>;
}

// ── Entwurf: Goldtusche exakt auf den Fugen des Fotos ──
function Blueprint({ project, index }) {
  const joints = JOINTS[index];
  const step = Math.min(14, 1500 / joints.length);
  const [dx, dy] = project.datum;
  return (
    <svg className="hero-bp" viewBox="0 0 1000 1250" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <g className="hero-bp-context">
        {project.context.map((d, i) => <path key={i} d={d} pathLength="1" style={{ '--d': `${200 + i * 40}ms` }} />)}
      </g>
      <g className="hero-bp-joints">
        {joints.map((d, i) => <path key={i} d={d} pathLength="1" style={{ '--d': `${Math.round(i * step)}ms` }} />)}
      </g>
      <g className="hero-bp-outline">
        {project.outline.map((d, i) => <path key={i} d={d} pathLength="1" style={{ '--d': `${400 + i * 200}ms` }} />)}
      </g>
      <g className="hero-bp-datum" transform={`translate(${dx} ${dy})`}>
        <path d="M-14 0H14M0 0L-9 -15H9Z" />
        <text x="22" y="-3">OK Belag ± 0,00</text>
      </g>
    </svg>
  );
}

// ── Main Header ───────────────────────────────────────
const Header = () => {
  const [ready, setReady] = useState(false);
  const [showSecondaryNavbar, setShowSecondaryNavbar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [active, setActive] = useState(0);
  const [phase, setPhase] = useState('draw');      // draw → sweep → idle
  const [interacted, setInteracted] = useState(false);
  const [hover, setHover] = useState(false);
  const [inView, setInView] = useState(true);
  const reduced = useReducedMotion();
  const navbarRef = useRef(null);
  const heroRef = useRef(null);
  const frameRef = useRef(null);
  const handleRef = useRef(null);
  const posRef = useRef(100);
  const anim = useRef({ raf: 0, timers: [] });
  const drag = useRef(false);

  // Reglerposition ohne Re-Render: 0 = alles fertig, 100 = alles Entwurf
  const setPos = useCallback((v) => {
    posRef.current = v;
    if (frameRef.current) frameRef.current.style.setProperty('--pos', v.toFixed(2));
    if (handleRef.current) {
      handleRef.current.setAttribute('aria-valuenow', String(Math.round(v)));
      handleRef.current.setAttribute('aria-valuetext', `${Math.round(v)} % Entwurf, ${Math.round(100 - v)} % fertig`);
    }
  }, []);

  const stopAnim = useCallback(() => {
    cancelAnimationFrame(anim.current.raf);
    anim.current.timers.forEach(clearTimeout);
    anim.current.timers = [];
  }, []);

  const tween = useCallback((to, ms, done) => {
    const from = posRef.current;
    const t0 = performance.now();
    const tick = (now) => {
      const k = Math.min(1, (now - t0) / ms);
      setPos(from + (to - from) * easeIO(k));
      if (k < 1) anim.current.raf = requestAnimationFrame(tick);
      else if (done) done();
    };
    anim.current.raf = requestAnimationFrame(tick);
  }, [setPos]);

  const later = (fn, ms) => { anim.current.timers.push(setTimeout(fn, ms)); };

  // Einstieg
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Weitere Referenzbilder vorladen
  useEffect(() => {
    const t = setTimeout(() => PROJECTS.slice(1).forEach((p) => { const i = new Image(); i.src = withPublic(p.src); }), 2500);
    return () => clearTimeout(t);
  }, []);

  // Sekundäre Navigation nach dem Hero (unverändertes Verhalten)
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (window.innerWidth > 768) setShowSecondaryNavbar(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    if (navbarRef.current) obs.observe(navbarRef.current);

    const heroObs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.05 });
    if (heroRef.current) heroObs.observe(heroRef.current);

    return () => {
      window.removeEventListener('resize', checkMobile);
      obs.disconnect();
      heroObs.disconnect();
    };
  }, []);

  // Pro Projekt: erst zeichnen, dann fährt die Baulinie durch und legt das fertige Ergebnis frei
  useEffect(() => {
    stopAnim();
    if (reduced) { setPos(REST); setPhase('idle'); return undefined; }
    if (!ready) { setPos(100); return undefined; }
    setPos(100);
    setPhase('draw');
    later(() => {
      setPhase('sweep');
      tween(0, 1900, () => later(() => tween(REST, 1100, () => setPhase('idle')), 350));
    }, DRAW_MS);
    return stopAnim;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduced, ready, stopAnim, tween, setPos]);

  // Automatisch weiter – nur bis zur ersten Interaktion
  const autoplay = !reduced && !interacted && !hover && inView && phase === 'idle';
  useEffect(() => {
    if (!autoplay) return undefined;
    const t = setTimeout(() => setActive((a) => (a + 1) % PROJECTS.length), HOLD_MS);
    return () => clearTimeout(t);
  }, [autoplay, active]);

  // Regler ziehen (Maus, Stift, Finger – vertikales Scrollen bleibt erhalten)
  const posFromEvent = (e) => {
    const r = frameRef.current.getBoundingClientRect();
    return clamp(((e.clientX - r.left) / r.width) * 100, 0, 100);
  };
  const takeOver = () => {
    stopAnim();
    setInteracted(true);
    setPhase('idle');
  };
  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    takeOver();
    drag.current = true;
    frameRef.current.setPointerCapture?.(e.pointerId);
    setPos(posFromEvent(e));
  };
  const onPointerMove = (e) => { if (drag.current) setPos(posFromEvent(e)); };
  const endDrag = () => { drag.current = false; };
  const onKeyDown = (e) => {
    const map = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5, PageDown: -20, PageUp: 20 };
    let v;
    if (e.key in map) v = posRef.current + map[e.key];
    else if (e.key === 'Home') v = 0;
    else if (e.key === 'End') v = 100;
    else return;
    e.preventDefault();
    takeOver();
    setPos(clamp(v, 0, 100));
  };

  const choose = (i) => {
    setInteracted(true);
    if (i === active) {
      // gleiches Projekt: Zeichnung erneut abspielen
      setActive(-1);
      requestAnimationFrame(() => setActive(i));
    } else setActive(i);
  };

  const shown = active < 0 ? null : active;
  const project = PROJECTS[shown ?? 0];
  const num = (n) => String(n).padStart(2, '0');
  const blueprint = useMemo(() => (shown === null ? null : <Blueprint project={PROJECTS[shown]} index={shown} />), [shown]);

  return (
    <>
      {!isMobile && showSecondaryNavbar && <SecondaryNavbar />}

      <div
        id="header-background"
        ref={heroRef}
        className={`hero-root${ready ? ' is-ready' : ''}${reduced ? ' is-reduced' : ''}`}
      >
        <div className="hero-grain" aria-hidden="true" />

        <div ref={navbarRef} className="hero-nav">
          <Navbar />
        </div>

        <div className="hero-body">
          {/* ── Text ── */}
          <div className="hero-copy">
            <p className="hero-eyebrow">
              <span className="hero-eyebrow-line" aria-hidden="true" />
              Garten- &amp; Landschaftsbau · Hannover
            </p>

            <h1 className="hero-title">
              <span className="hero-line"><span>Erst gezeichnet.</span></span>
              <span className="hero-line">
                <span>
                  Dann <em>gebaut.</em>
                  <svg className="hero-underline" viewBox="0 0 300 24" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M4 16C60 6 140 4 200 9C240 12 270 14 296 8" pathLength="1" />
                  </svg>
                </span>
              </span>
            </h1>

            <p className="hero-sub">
              Pflasterarbeiten, Terrassen und Gartengestaltung aus einer Hand.
              Jede Fuge ist geplant, bevor sie verlegt wird – und am Ende liegt sie genau dort.
            </p>

            <div className="hero-ctas">
              <a href="#traumgarten" className="hero-btn hero-btn--primary">
                <span>Deinen Garten zeichnen</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <a href="#projekte" className="hero-btn hero-btn--ghost">
                <span>Projekte ansehen</span>
              </a>
            </div>
          </div>

          {/* ── Plan und Ergebnis, übereinander ── */}
          <figure
            className={`hero-figure is-${phase}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <div
              ref={frameRef}
              className="hero-frame"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onLostPointerCapture={endDrag}
            >
              {shown !== null && (
                <div className="hero-stage" key={shown}>
                  {/* Entwurf */}
                  <div className="hero-draft">
                    <img src={withPublic(project.src)} alt="" draggable="false" />
                    <div className="hero-draft-tint" />
                    {blueprint}
                  </div>
                  {/* Fertig */}
                  <div className="hero-done">
                    <img
                      src={withPublic(project.src)}
                      alt={`Referenzprojekt von EG-Bau: ${project.title}`}
                      draggable="false"
                    />
                  </div>
                  {project.marks.map((m) => (
                    <span
                      key={m.t}
                      className="hero-mark"
                      style={{ left: `${m.x}%`, top: `${m.y}%`, '--x': m.x }}
                      aria-hidden="true"
                    >
                      <i />
                      <span><strong>{m.t}</strong><small>{m.s}</small></span>
                    </span>
                  ))}
                </div>
              )}

              <span className="hero-chip hero-chip--draft" aria-hidden="true">Entwurf</span>
              <span className="hero-chip hero-chip--done" aria-hidden="true">Fertig</span>

              <div className="hero-handle" aria-hidden="true"><span /></div>
              <button
                ref={handleRef}
                type="button"
                className="hero-knob"
                role="slider"
                aria-label="Vergleich Entwurf und fertiges Projekt"
                aria-valuemin={0}
                aria-valuemax={100}
                onKeyDown={onKeyDown}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 7l-5 5 5 5M15 7l5 5-5 5" />
                </svg>
              </button>

              <span className="hero-crop hero-crop--tl" aria-hidden="true" />
              <span className="hero-crop hero-crop--tr" aria-hidden="true" />
              <span className="hero-crop hero-crop--bl" aria-hidden="true" />
              <span className="hero-crop hero-crop--br" aria-hidden="true" />
            </div>

            <figcaption className="hero-caption">
              <div className="hero-tabs" role="group" aria-label="Referenzprojekte">
                {PROJECTS.map((p, i) => (
                  <button
                    key={p.src}
                    type="button"
                    className={`hero-tab${i === shown ? ' is-on' : ''}`}
                    aria-pressed={i === shown}
                    onClick={() => choose(i)}
                  >
                    <em>{num(i + 1)}</em> {p.tab}
                    {i === shown && autoplay && (
                      <span className="hero-tab-progress" style={{ animationDuration: `${HOLD_MS}ms` }} aria-hidden="true" />
                    )}
                  </button>
                ))}
              </div>
              <span className="hero-caption-text" key={shown}>
                <span>{project.title}</span>
                <small>{project.note}</small>
              </span>
              <span className="hero-hint" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M9 7l-5 5 5 5M15 7l5 5-5 5" /></svg>
                Regler ziehen
              </span>
            </figcaption>
          </figure>
        </div>

        {/* ── Schriftfeld ── */}
        <dl className="hero-facts">
          <div>
            <dt>Projekte</dt>
            <dd><Counter to={15} suffix="+" start={ready} instant={reduced} /></dd>
          </div>
          <div>
            <dt>Erfahrung</dt>
            <dd><Counter to={3} suffix="+" start={ready} instant={reduced} /> <small>Jahre</small></dd>
          </div>
          <div>
            <dt>Zufriedene Kunden</dt>
            <dd><Counter to={97} suffix="%" start={ready} instant={reduced} /></dd>
          </div>
          <div className="hero-facts-cta">
            <dt>Region Hannover</dt>
            <dd><a href="#contact">Kostenlose Erstberatung</a></dd>
          </div>
        </dl>

        <a href="#dienstleistungen" className="hero-scroll" aria-label="Weiter zu den Leistungen">
          <span className="hero-scroll-track"><span /></span>
        </a>
      </div>
    </>
  );
};

export default Header;
